import { formatDate, formatMoney, RESERVATION_STATUS_META } from '@/lib/hebergement';
import type { HebergementReservation } from '@/lib/hebergement';

interface Props {
  stats: {
    arrivalsToday: number;
    departuresToday: number;
    currentGuests: number;
    upcoming: number;
    occupiedUnits: number;
    availableUnits: number;
    revenue: number;
    pendingPayments: number;
    occupancyRate: number;
  };
  upcomingReservations: HebergementReservation[];
  currency: string;
  propertyName: (id: number | null) => string;
  unitName: (id: number | null) => string;
  onGoToTab: (tab: string) => void;
  onNewReservation: () => void;
}

export default function OverviewView({
  stats, upcomingReservations, currency, propertyName, unitName, onGoToTab, onNewReservation,
}: Props) {
  const cards = [
    { icon: 'ri-login-box-line', label: "Arrivées aujourd'hui", value: stats.arrivalsToday, color: 'bg-primary-100 text-primary-700' },
    { icon: 'ri-logout-box-line', label: "Départs aujourd'hui", value: stats.departuresToday, color: 'bg-accent-100 text-accent-700' },
    { icon: 'ri-user-heart-line', label: 'Voyageurs présents', value: stats.currentGuests, color: 'bg-secondary-100 text-secondary-700' },
    { icon: 'ri-calendar-schedule-line', label: 'Réservations à venir', value: stats.upcoming, color: 'bg-primary-50 text-primary-600' },
    { icon: 'ri-checkbox-circle-line', label: 'Unités libres', value: stats.availableUnits, color: 'bg-accent-50 text-accent-700' },
    { icon: 'ri-door-closed-line', label: 'Unités occupées', value: stats.occupiedUnits, color: 'bg-secondary-50 text-secondary-700' },
    { icon: 'ri-money-dollar-circle-line', label: 'Revenus réservations', value: formatMoney(stats.revenue, currency), color: 'bg-accent-100 text-accent-700' },
    { icon: 'ri-hourglass-line', label: 'En attente de paiement', value: formatMoney(stats.pendingPayments, currency), color: 'bg-amber-100 text-amber-700' },
  ];

  return (
    <div className="space-y-5">
      {/* Taux d'occupation */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-hotel-line text-2xl"></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-foreground-900">Taux d&apos;occupation actuel</h3>
            <span className="text-2xl font-bold font-heading text-foreground-950">{stats.occupancyRate}%</span>
          </div>
          <div className="mt-2 h-2 w-full rounded-full bg-background-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-500"
              style={{ width: `${Math.min(100, stats.occupancyRate)}%` }}
            ></div>
          </div>
        </div>
        <button
          onClick={onNewReservation}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>Nouvelle réservation
        </button>
      </div>

      {/* Cartes stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-9 h-9 rounded-lg ${c.color} flex items-center justify-center mb-3`}>
              <i className={`${c.icon} text-lg`}></i>
            </div>
            <div className="text-xl font-bold font-heading text-foreground-950 truncate">{c.value}</div>
            <div className="text-xs text-foreground-500 mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Prochaines réservations */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
            <i className="ri-calendar-check-line text-primary-500"></i>
            Prochaines réservations
          </h3>
          <button
            onClick={() => onGoToTab('reservations')}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            Voir tout <i className="ri-arrow-right-line"></i>
          </button>
        </div>
        {upcomingReservations.length === 0 ? (
          <div className="py-12 text-center">
            <i className="ri-calendar-line text-3xl text-foreground-300"></i>
            <p className="mt-2 text-sm text-foreground-500">Aucune réservation à venir</p>
          </div>
        ) : (
          <div className="divide-y divide-background-200/70">
            {upcomingReservations.map((r) => {
              const meta = RESERVATION_STATUS_META[r.status];
              return (
                <div key={r.id} className="flex items-center gap-3 px-5 py-3.5">
                  <div className="w-10 h-10 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-smile-line"></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground-900 truncate">
                      {r.guest_name || 'Client'}
                    </div>
                    <div className="text-xs text-foreground-500 truncate">
                      {propertyName(r.property_id)} · {unitName(r.unit_id)}
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="text-xs text-foreground-600 whitespace-nowrap">
                      {formatDate(r.check_in)} → {formatDate(r.check_out)}
                    </div>
                    <div className="text-xs text-foreground-400">{r.nights} nuit{r.nights > 1 ? 's' : ''} · {r.guests} voyageur{r.guests > 1 ? 's' : ''}</div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${meta.className}`}>
                    {meta.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}